import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { withRouter } from 'utils';
import { Button, Icon } from '@mui/material';
import {
  StoreUtil,
  PhoneUtil,
} from 'doctivity-shared/utils';

import {
  DialogForm,
  LoadingView,
} from 'components';

import {
  deleteContact,
} from 'store/actions/contactsActions';
import {
  listUsers,
} from 'store/actions/usersActions';
import {
  listProviderComments
} from 'store/actions/providerCommentsActions';

import { ContactDeleteDialog } from './ContactDeleteDialog';

const styles = (theme) => ({
  deleteIcon: {
    color: theme.palette.primary.main,
  },
  button: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
});

const schema = {
  type: 'object',
  required: ['first_name', 'last_name'],
  properties: {
    first_name: { type: 'string', title: 'First Name' },
    last_name: { type: 'string', title: 'Last Name' },
    title: { type: 'string', title: 'Title' },
    company: { type: 'string', title: 'Company', maxLength: 75 },
    email: { type: 'string', title: 'Email' },
    phone: {
      type: 'string',
      title: 'Phone',
      mask: 'phone-ext',
    },
    mobile_phone: {
      type: 'string',
      title: 'Mobile',
      mask: 'phone-ext',
    },
    fax: {
      type: 'string',
      title: 'Fax',
      mask: 'phone-ext',
    },
    owner_user_id: {
      type: 'string',
      title: 'Owner',
      enumNames: [],
      enum: [],
    },
  },
};

const uiSchema = {
  first_name: {
    'ui:classNames': 'two-column-field',
  },
  last_name: {
    'ui:classNames': 'two-column-field',
  },
  title: {
    'ui:classNames': 'two-column-field',
  },
  company: {
    'ui:classNames': 'two-column-field',
  },
  email: {
    'ui:classNames': 'two-column-field',
  },
  phone: {
    'ui:classNames': 'two-column-field',
  },
  mobile_phone: {
    'ui:classNames': 'two-column-field',
  },
  fax: {
    'ui:classNames': 'two-column-field',
  },
  owner_user_id: {
    'ui:classNames': 'two-column-field',
  },
};

const NEW_CONTACT = {
};

class ContactEditDialog extends Component {
  constructor(props) {
    super(props);

    this.state = {
      deleteDialogOpen: false,
    };
  }

  componentDidMount() {
    if (StoreUtil.needsLoadLongCache(this.props.users)
      || (this.props.usersClientId !== this.props.app.selectedClient)) {
      this.fetchUsers();
    }

    this.fetchProviderComments();
  }

  componentDidUpdate(prevProps) {
    const {
      providerComments
    } = this.props;

    if (StoreUtil.needsLoadLongCache(this.props.users)
      || (this.props.usersClientId !== this.props.app.selectedClient)) {
      this.fetchUsers();
    }

    if(!providerComments ||
      this.props.contactData?.id !== prevProps?.contactData?.id || 
      (providerComments.data?.length !== 0 &&
         StoreUtil.needsLoadLongCache(providerComments))) {
      this.fetchProviderComments();
    }
  }

  fetchUsers() {
    this.props.dispatch(listUsers(this.props.app.selectedClient));
  }

  fetchProviderComments() {
    const contactId = this.getContactId();
    if(contactId) {
      this.props.dispatch(
        listProviderComments(
          null,
          contactId,
          null,
          this.props.app.selectedClient
        )
      );
    }
  }

  getContactId() {
    const {
      contactData
    } = this.props;

    return contactData ? parseInt(contactData.id, 10) : null;
  }

  render() {
    const { classes, open, contactData, providerComments, submitText } = this.props;

    if (!StoreUtil.isLoaded(this.props.users)) {
      return <LoadingView />;
    }

    const users = StoreUtil.getData(this.props.users);
    
    // Create a fresh schema object each render
    const formSchema = {
      type: 'object',
      required: ['first_name', 'last_name'],
      properties: {
        first_name: { type: 'string', title: 'First Name' },
        last_name: { type: 'string', title: 'Last Name' },
        title: { type: 'string', title: 'Title' },
        company: { type: 'string', title: 'Company', maxLength: 75 },
        email: { type: 'string', title: 'Email' },
        phone: {
          type: 'string',
          title: 'Phone',
          mask: 'phone-ext',
        },
        mobile_phone: {
          type: 'string',
          title: 'Mobile',
          mask: 'phone-ext',
        },
        fax: {
          type: 'string',
          title: 'Fax',
          mask: 'phone-ext',
        },
        owner_user_id: {
          type: 'number',  // Keep as number
          title: 'Owner',
          oneOf: users.map((u) => ({
            const: u.id,  // Use actual number ID
            title: `${u.last_name}, ${u.first_name}`
          })),
        },
      },
    };

    // Set default owner for new contacts
    const newContact = {};
    if (this.props.user && !this.props.user.is_admin) {
      newContact.owner_user_id = this.props.user.id;  // Keep as number
    }

    // Don't convert owner_user_id - keep it as number
    const formData = contactData || newContact;

    return (
      <div>
        <DialogForm
          key={`contact-${contactData?.id || 'new'}`}
          title={
            contactData && contactData.id > 0 ? 'Edit Contact' : 'Add Contact'
          }
          open={open}
          formData={formData}
          schema={formSchema}
          uiSchema={uiSchema}
          onClose={this.props.onClose}
          onFormSubmit={this.onSave}
          onSecondarySubmit={this.onOpenDeleteDialog}
          submitText={submitText}
          secondaryText={providerComments?.data?.length === 0 ? <Button
            className={classes.button}
            color='secondary'
            margin='normal'
            variant='text'
            onClick={this.onOpenDeleteDialog}
          >
            <Icon className={classes.deleteIcon}>delete</Icon>
          </Button> : undefined }
          status={
            this.state.userDialogError ? this.state.userDialogErrorMessage : ''
          }
          statusIsError
        />
        {this.state.deleteDialogOpen && (
          <ContactDeleteDialog
            open={this.state.deleteDialogOpen}
            onDelete={this.onDeleteContact}
            onClose={this.onCloseDeleteDialog}
            contactData={contactData}
          />
        )}
      </div>
    );
  }

  onOpenDeleteDialog = () => {
    this.setState({
      deleteDialogOpen: true,
    });
  };

  onCloseDeleteDialog = () => {
    this.setState({
      deleteDialogOpen: false,
    });
  };

  onDeleteContact = (contact) => {
    this.props.dispatch(deleteContact(contact.id));
    this.onCloseDeleteDialog();
    this.props.router.navigate(-1);
  }

  onSave = (data) => {
    
    if (data.phone) {
      data.phone = PhoneUtil.unformat(data.phone);
      if (!data.phone || !PhoneUtil.isValid(data.phone)) {
        this.setState({
          userDialogError: true,
          userDialogErrorMessage: 'Invalid phone number.',
        });
        return;
      }
    }
    if (data.mobile_phone) {
      data.mobile_phone = PhoneUtil.unformat(data.mobile_phone);
      if (!data.mobile_phone || !PhoneUtil.isValid(data.mobile_phone)) {
        this.setState({
          userDialogError: true,
          userDialogErrorMessage: 'Invalid mobile phone number.',
        });
        return;
      }
    }

    this.props.onSave(data);
  };
}

ContactEditDialog.propTypes = {
  router: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  submitText: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  contactData: PropTypes.object,
  user: PropTypes.object,
  users: PropTypes.object,
  app: PropTypes.object,
  usersClientId: PropTypes.number,
  providerComments: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    user: state.user,
    users: StoreUtil.get(state.users, StoreUtil.COMMON_LIST),
    usersClientId: state.users.listClientId,
    app: state.app,
    providerComments: StoreUtil.get(state.providerComments, StoreUtil.COMMON_LIST),
  };
}

const styled = withStyles(styles)(ContactEditDialog);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as ContactEditDialog };
